/**
 * The Link realtor referral carried by Link's Apply Now invite
 * (`?leadId=…&realtorId=…&realtorAccountId=…`) and by its OTC register invite
 * (`/register?otc=…&leadId=…&realtorId=…&realtorContactId=…`), forwarded on loan create so
 * the API can stamp the loan onto Link's lead and add the realtor to the Vesta loan (NP-308).
 *
 * Kept across registration and login the way `?lo=` is, because the create happens
 * pages after the borrower landed. Every value comes off a URL or out of storage, so
 * both are untrusted and re-validated on read.
 */

import { searchThroughReturnUrl } from '@/lib/returnUrlSearch';
import type { LinkReferral } from '@/types/wizard';

/** Exported so tests assert against the real key rather than mirroring a literal. */
export const LINK_REFERRAL_STORAGE_KEY = 'path_link_referral';

const REFERRAL_KEYS = ['leadId', 'realtorId', 'realtorAccountId', 'realtorContactId'] as const;

/** The API binds these as int32. */
const INT32_MAX = 2_147_483_647;

/**
 * A value that is not a number fails JSON binding and rejects the whole create with a
 * 400, and a number past int32 does the same — so anything but a positive whole number
 * in range is dropped rather than risking the loan.
 */
function positiveId(raw: unknown): number | null {
  const text = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value > 0 && value <= INT32_MAX ? value : null;
}

function toReferral(source: Partial<Record<(typeof REFERRAL_KEYS)[number], unknown>>): LinkReferral | null {
  const referral: LinkReferral = {};
  for (const key of REFERRAL_KEYS) {
    const value = positiveId(source[key]);
    if (value !== null) referral[key] = value;
  }
  return Object.keys(referral).length > 0 ? referral : null;
}

export function linkReferralFromSearch(search: string | null | undefined): LinkReferral | null {
  return searchThroughReturnUrl(search, (params) =>
    toReferral(Object.fromEntries(REFERRAL_KEYS.map((key) => [key, params.get(key)]))),
  );
}

/**
 * Replaces the stored referral wholesale: a newer invite is a different lead, and
 * merging would pair its lead with an older link's realtor.
 */
export function storeLinkReferral(referral: LinkReferral): void {
  try {
    localStorage.setItem(LINK_REFERRAL_STORAGE_KEY, JSON.stringify(referral));
  } catch {
    /* storage unavailable */
  }
}

function readStoredLinkReferral(): LinkReferral | null {
  try {
    const raw = localStorage.getItem(LINK_REFERRAL_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? toReferral(parsed) : null;
  } catch {
    return null;
  }
}

/**
 * The referral to send on create: the link the borrower is standing on, else the one
 * captured when they landed. The current URL is read too so a borrower with storage
 * disabled still carries it.
 */
export function readLinkReferralForCreate(): LinkReferral | null {
  if (typeof window === 'undefined') return null;
  return linkReferralFromSearch(window.location.search) ?? readStoredLinkReferral();
}

/**
 * True while an invite link's lead has not yet been spent on a create — the borrower is
 * answering a Snap/Link invitation, so their next create is that invite's application,
 * not a reason to resume an older one of their own.
 */
export function hasPendingInviteLead(): boolean {
  return readLinkReferralForCreate()?.leadId != null;
}

/** After a successful create, so a second loan is not attributed to the same lead. */
export function clearLinkReferral(): void {
  try {
    localStorage.removeItem(LINK_REFERRAL_STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}
